import type { SimulationActivity } from '../../types/simulation';
import { inferActivityStopKind } from './inferActivityStopKind';

const EPOCH_SENTINEL_MS_MAX = 86_400_000;

/** Plain-English tooltip copy; keep in sync with `resolveTripTravelDisplay` branches. */
export const TRAVEL_TIME_TOOLTIP_IN_VEHICLE =
  'Time from the start of the pickup stop to the end of the drop-off stop for this trip, using the times on each leg from the simulation activities.';

export const TRAVEL_TIME_TOOLTIP_WALL_CLOCK =
  'Time from the earliest pickup activity time to the latest drop-off activity time for this trip on the map.';

export const TRAVEL_TIME_TOOLTIP_MISSING =
  'Travel time is not available because pickup or drop-off times are missing.';

export type TravelTimeMethod = 'in_vehicle' | 'wall_clock' | 'missing';

// Ride-level trip identity only. The passenger/user id (`external_user_reference_id`) is
// deliberately EXCLUDED — it is shared across every trip a user takes (and every co-rider on a
// shared stop), so including it would merge unrelated trips into one focus selection. User id is
// still resolved separately for display in `passengerUserIdFromActivitiesAndRidePoint`.
const TRIP_NESTED_ID_FIELDS = [
  'ride_id',
  'request_id',
  'driver_ride_id',
  'rider_ride_id',
  'external_reference_id',
  'booking_id',
  'trip_id',
  'requestId',
  'driverRideId',
  'riderRideId',
  'rideId',
  'externalReferenceId',
] as const;

function normalizeTripId(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  const s = String(value).trim();
  return s.length > 0 ? s : null;
}

function pushTripKeysFromRecordInto(out: string[], rec: Record<string, unknown>): void {
  for (const k of TRIP_NESTED_ID_FIELDS) {
    const s = normalizeTripId(rec[k]);
    if (s) out.push(s);
  }
}

function collectNestedUserKeysFromActivity(activity: SimulationActivity): string[] {
  const out: string[] = [];
  const raw = activity as Record<string, unknown>;
  for (const key of ['users_picked_up', 'users_dropped_off', 'passengers'] as const) {
    const arr = raw[key];
    if (!Array.isArray(arr)) continue;
    for (const item of arr) {
      if (item != null && typeof item === 'object') {
        pushTripKeysFromRecordInto(out, item as Record<string, unknown>);
      }
    }
  }
  return out;
}

/** Trip / request ids from nested `trips[]` (matches ride point `ride_id`, `external_reference_id`, etc.). */
function collectTripNestedIdsFromActivity(activity: SimulationActivity): string[] {
  const out: string[] = [];
  const raw = activity as Record<string, unknown>;
  const trips = raw.trips;
  if (!Array.isArray(trips)) return out;
  for (const trip of trips) {
    if (trip == null || typeof trip !== 'object') continue;
    const tr = trip as Record<string, unknown>;
    const r1 = normalizeTripId(tr.rider_ride_id);
    if (r1) out.push(r1);
    const ride = tr.ride;
    if (ride != null && typeof ride === 'object') {
      const rObj = ride as Record<string, unknown>;
      for (const key of ['ride_id', 'external_reference_id', 'request_id'] as const) {
        const id = normalizeTripId(rObj[key]);
        if (id) out.push(id);
      }
    }
  }
  return out;
}

/** Same id candidates as SimulationResultsMenu `collectActivityIdCandidates`, plus trip leg ids. */
export function collectActivityIdCandidatesForTripFocus(activity: SimulationActivity): string[] {
  const a = activity as Record<string, unknown>;
  // Ride-level ids only — user id (`external_user_reference_id`) intentionally omitted (see
  // TRIP_NESTED_ID_FIELDS note), so selecting one trip never expands to a user's other trips.
  const raw = [
    activity.ride_id,
    activity.request_id,
    a.requestId,
    a.rider_ride_id,
    a.driver_ride_id,
    a.external_reference_id,
    a.externalReferenceId,
  ]
    .map(normalizeTripId)
    .filter((x): x is string => x != null);
  return Array.from(
    new Set([...raw, ...collectNestedUserKeysFromActivity(activity), ...collectTripNestedIdsFromActivity(activity)])
  );
}

/**
 * Ride-point counterpart of {@link collectActivityIdCandidatesForTripFocus}: the ride-level trip
 * identity ids for a ride point (plus nested `passengers[]` ride ids), so ride points and
 * activities index under the same trip. Excludes the passenger/user id for the same reason.
 */
export function collectRidePointIdCandidatesForTripFocus(point: Record<string, unknown>): string[] {
  const raw = [
    point.ride_id,
    point.request_id,
    point.external_reference_id,
    point.driver_ride_id,
    point.rider_ride_id,
    point.externalReferenceId,
    point.requestId,
    point.driverRideId,
    point.riderRideId,
    point.rideId,
  ]
    .map(normalizeTripId)
    .filter((x): x is string => x != null);

  const nested: string[] = [];
  const passengers = point.passengers;
  if (Array.isArray(passengers)) {
    for (const item of passengers) {
      if (item != null && typeof item === 'object') {
        pushTripKeysFromRecordInto(nested, item as Record<string, unknown>);
      }
    }
  }
  return Array.from(new Set([...raw, ...nested]));
}

export function activityBelongsToTripMatchKeys(activity: SimulationActivity, keySet: Set<string>): boolean {
  return collectActivityIdCandidatesForTripFocus(activity).some((k) => keySet.has(k));
}

function isEpochSentinelIso(iso: unknown): boolean {
  if (typeof iso !== 'string' || !iso) return false;
  const t = Date.parse(iso);
  return Number.isFinite(t) && t < EPOCH_SENTINEL_MS_MAX;
}

function parseTimeMs(iso: unknown): number | null {
  if (typeof iso !== 'string' || !iso) return null;
  if (isEpochSentinelIso(iso)) return null;
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? null : parsed;
}

/** All trip-level ids that can tie a leg to `matchKeys` (ride id vs batch external ref, etc.). */
function tripLegIdCandidates(trip: Record<string, unknown>): string[] {
  const out: string[] = [];
  const rider = normalizeTripId(trip.rider_ride_id);
  if (rider) out.push(rider);
  const ride = trip.ride;
  if (ride != null && typeof ride === 'object') {
    const r = ride as Record<string, unknown>;
    for (const key of ['ride_id', 'external_reference_id', 'request_id'] as const) {
      const id = normalizeTripId(r[key]);
      if (id) out.push(id);
    }
  }
  return out;
}

function tripMatchesKeys(trip: Record<string, unknown>, keySet: Set<string>): boolean {
  return tripLegIdCandidates(trip).some((c) => keySet.has(c));
}

/**
 * In-vehicle minutes from nested `trips` PICK/DROP legs when both exist for the same ride id as matchKeys.
 */
export function inVehicleTravelMinutesFromActivities(
  matchKeys: string[],
  activities: SimulationActivity[]
): number | null {
  const keySet = new Set(matchKeys.filter(Boolean));
  if (keySet.size === 0) return null;

  const matching = activities.filter((a) => activityBelongsToTripMatchKeys(a, keySet));
  let pickStartMs: number | null = null;
  let dropEndMs: number | null = null;

  for (const act of matching) {
    const raw = act as Record<string, unknown>;
    const trips = raw.trips;
    if (!Array.isArray(trips)) continue;
    for (const trip of trips) {
      if (trip == null || typeof trip !== 'object') continue;
      const tr = trip as Record<string, unknown>;
      if (!tripMatchesKeys(tr, keySet)) continue;
      const at = (tr.activity_type ?? '').toString().toUpperCase();
      if (at === 'PICK') {
        const start =
          parseTimeMs(tr.activity_start_time) ?? parseTimeMs(tr.activity_end_time);
        if (start != null) {
          pickStartMs = pickStartMs == null ? start : Math.min(pickStartMs, start);
        }
      }
      if (at === 'DROP') {
        const end = parseTimeMs(tr.activity_end_time) ?? parseTimeMs(tr.activity_start_time);
        if (end != null) {
          dropEndMs = dropEndMs == null ? end : Math.max(dropEndMs, end);
        }
      }
    }
  }

  if (pickStartMs != null && dropEndMs != null && dropEndMs > pickStartMs) {
    return Math.round(((dropEndMs - pickStartMs) / 1000 / 60) * 10) / 10;
  }
  return null;
}

function parseActivityTimestampMsForWallClock(activity: SimulationActivity): number | null {
  const a = activity as Record<string, unknown>;
  const candidates = [
    activity.activity_start_time,
    activity.activity_start,
    activity.min_activity_start_time,
    activity.start_time,
    activity.time,
    a.activity_end_time,
  ];
  for (const value of candidates) {
    if (typeof value !== 'string') continue;
    if (isEpochSentinelIso(value)) continue;
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return null;
}

function wallClockTravelMinutesFromActivities(matchKeys: string[], activities: SimulationActivity[]): number | null {
  const keySet = new Set(matchKeys.filter(Boolean));
  if (keySet.size === 0) return null;
  const matching = activities.filter((a) => activityBelongsToTripMatchKeys(a, keySet));
  const pickupMs: number[] = [];
  const dropoffMs: number[] = [];
  for (const a of matching) {
    const kind = inferActivityStopKind(a);
    const ms = parseActivityTimestampMsForWallClock(a);
    if (ms == null) continue;
    if (kind === 'pick') pickupMs.push(ms);
    if (kind === 'drop') dropoffMs.push(ms);
  }
  const pickup = pickupMs.length ? Math.min(...pickupMs) : null;
  const dropoff = dropoffMs.length ? Math.max(...dropoffMs) : null;
  if (pickup != null && dropoff != null && dropoff > pickup) {
    return Math.round(((dropoff - pickup) / 1000 / 60) * 10) / 10;
  }
  return null;
}

export function resolveTripTravelDisplay(
  matchKeys: string[],
  activities: SimulationActivity[]
): {
  minutes: number | null;
  formatted: string;
  method: TravelTimeMethod;
  tooltip: string;
} {
  const inVeh = inVehicleTravelMinutesFromActivities(matchKeys, activities);
  if (inVeh != null) {
    return {
      minutes: inVeh,
      formatted: `${inVeh} min`,
      method: 'in_vehicle',
      tooltip: TRAVEL_TIME_TOOLTIP_IN_VEHICLE,
    };
  }
  const wall = wallClockTravelMinutesFromActivities(matchKeys, activities);
  if (wall != null) {
    return {
      minutes: wall,
      formatted: `${wall} min`,
      method: 'wall_clock',
      tooltip: TRAVEL_TIME_TOOLTIP_WALL_CLOCK,
    };
  }
  return {
    minutes: null,
    formatted: '—',
    method: 'missing',
    tooltip: TRAVEL_TIME_TOOLTIP_MISSING,
  };
}

function firstStringUserIdFromUsersArray(arr: unknown): string | null {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const first = arr[0];
  if (typeof first === 'string' && first.trim()) return first.trim();
  return null;
}

/**
 * Primary passenger / user id for the focused trip (ride point first, then PICK trip ride payload, then users_picked_up on matching activities).
 */
export function passengerUserIdFromActivitiesAndRidePoint(
  matchKeys: string[],
  activities: SimulationActivity[],
  ridePoint?: Record<string, unknown> | null
): string {
  if (ridePoint) {
    const ext = normalizeTripId(ridePoint.external_user_reference_id);
    if (ext) return ext;
    const uid = normalizeTripId(ridePoint.user_id);
    if (uid) return uid;
  }

  const keySet = new Set(matchKeys.filter(Boolean));
  if (keySet.size === 0) return '—';

  for (const act of activities) {
    const raw = act as Record<string, unknown>;
    const trips = raw.trips;
    if (Array.isArray(trips)) {
      for (const trip of trips) {
        if (trip == null || typeof trip !== 'object') continue;
        const tr = trip as Record<string, unknown>;
        const at = (tr.activity_type ?? '').toString().toUpperCase();
        if (at !== 'PICK' && at !== 'DROP') continue;
        if (!tripMatchesKeys(tr, keySet)) continue;
        const ride = tr.ride;
        if (ride != null && typeof ride === 'object') {
          const eu = normalizeTripId((ride as Record<string, unknown>).external_user_reference_id);
          if (eu) return eu;
        }
      }
    }
  }

  for (const act of activities) {
    if (!activityBelongsToTripMatchKeys(act, keySet)) continue;
    const raw = act as Record<string, unknown>;
    const fromArr = firstStringUserIdFromUsersArray(raw.users_picked_up);
    if (fromArr) return fromArr;
  }

  return '—';
}
